import type { Mutable } from 'utility-types'

import { areCybusResourceDeviationsEqual, Capability, type CybusResourceDeviation } from '../../../../../../domain'

import type { PageSubscriptionsTypes, SubscriptionFilterArgs } from '../../../../../../application'

import { type BackendQueryParameters, mapToStatusType } from '../../../../../Connectware'
import { isLastPage } from '../../../mappers'
import { BaseSubscriptionStrategy } from '../Base'

export class ResourceDeviationsStrategy extends BaseSubscriptionStrategy<'resourceDeviation', never> {
    protected override readonly supportedFilters: (keyof SubscriptionFilterArgs)[] = ['service']

    protected override readonly areEquals = areCybusResourceDeviationsEqual

    override readonly requiredPermissions = [Capability.DEVIATIONS_READ]

    override async retrieveAll ({ service }: SubscriptionFilterArgs): Promise<PageSubscriptionsTypes['resourceDeviation'][]> {
        const deviations: CybusResourceDeviation[] = []
        let isDone = false
        let page = 0

        while (!isDone) {
            const queryParams: Mutable<BackendQueryParameters<'/api/v2/resources/states/deviations', 'GET'>> = { page: `${page + 1}` }
            if (service) {
                queryParams.serviceIds = [service]
            }

            type ResponseTupple = [additions: CybusResourceDeviation[], isLast: boolean]
            const [additions, isLast] = await this.request<'/api/v2/resources/states/deviations', 'GET', ResponseTupple>({
                path: '/api/v2/resources/states/deviations',
                method: 'GET',
                authenticate: true,
                capability: Capability.DEVIATIONS_READ,
                queryParams,
                handlers: {
                    200: async (response) => {
                        const { data, meta } = await response.getJson()

                        return [
                            data.map(({ resource: { id }, info, state: { current, desired } }) => ({
                                actual: mapToStatusType(current),
                                expected: mapToStatusType(desired),
                                resourceId: id,
                                reason: info || null,
                            })),
                            isLastPage(meta.pagination),
                        ]
                    },
                    503: ResourceDeviationsStrategy.createServerNotAvailableHandler('Resources Status Tracking is not running as expected'),
                },
            })

            deviations.push(...additions)
            isDone = isLast
            page++
        }

        return deviations
    }

    createAllChangeListener ({ service }: SubscriptionFilterArgs, handler: VoidFunction): Promise<VoidFunction> {
        if (!service) {
            /** Subscribe to deviations across all services */
            return this.rstAdapter.subscribeToServicesDeviationsCount('resourceDeviation', handler)
        }

        /** Subscribe to deviations on the service used as filter for this listing of resources */
        return this.rstAdapter.subscribeToServiceDeviationsCount('resourceDeviation', service, handler)
    }
}
